import { Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, Snackbar, Stack, TextField } from "@mui/material";
import React, { useEffect, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import CheckIcon from '@mui/icons-material/Check';
import CategoryAutocomplete from "./CategoryAutocomplete";
import UnitSelect from "./UnitSelect";
import { Item } from "../Services/Item/ItemApi";
import { useEditItem } from "../Services/Item/ItemHooks";

interface EditItemFormProps {
    open: boolean
    onClose: () => void
    onSaved: (item: Item) => void
    initialItem?: Item
}

export default function EditItemForm({ open, onClose, onSaved, initialItem }: EditItemFormProps) {
    const [displaySuccessBar, setDisplaySuccessBar] = useState(false);

    const { register, handleSubmit, reset, setError, watch, setValue, control, formState: { errors } } = useForm<Item>({
        defaultValues: initialItem ?? {
            name: "", category: { name: "" }, defaultQuantity:undefined
        }
    });

    const addItemMutation = useEditItem();

    useEffect(() => {
        reset();
    }, [initialItem, reset]);

    const onSubmit = (newItem: Item) => {
        if (!newItem.category || newItem.category.name === "") {
            setError("category", { message: "Category is required" });
            return;
        }
        addItemMutation.mutate(newItem, {
            onSuccess: () => {
                setDisplaySuccessBar(true);
                reset();
                addItemMutation.reset();
                onSaved(newItem);
            }
        });
    };

    const handleSnackbarClose = () => setDisplaySuccessBar(false);

    return (
        <>
            <Dialog
                open={open}
                onClose={onClose}
            >
                <DialogTitle>{"Add Item"}</DialogTitle>
                <DialogContent>
                    <Box sx={{ my: 1 }}>
                    <Stack spacing={1}>
                        <TextField
                            fullWidth
                            label="Name"
                            {...register("name", { required: "Name is required" })}
                            error={!!errors.name}
                            helperText={errors.name ? errors.name.message : ""}
                        />
                        <FormControl fullWidth>
                            <Controller
                                name="category"
                                control={control}
                                render={({ field, fieldState }) => (
                                    <CategoryAutocomplete
                                        {...field}
                                        error={fieldState.error}
                                    />
                                )}
                            />
                        </FormControl>
                        <FormControl fullWidth>
                            <Controller
                                name="defaultUnit"
                                control={control}
                                rules={{ required: "Default Unit is required" }}
                                render={({ field, fieldState }) => (
                                    <UnitSelect
                                        {...field}
                                        error={fieldState.error}
                                    />
                                )}
                            />
                        </FormControl>
                        <TextField
                            fullWidth
                            label="Default Quantity"
                            type="number"
                                {...register("defaultQuantity", { valueAsNumber: true })}
                            error={!!errors.defaultQuantity}
                            helperText={errors.defaultQuantity ? errors.defaultQuantity.message : ""}
                        />
                        </Stack>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleSubmit(onSubmit)}>Add Item</Button>
                </DialogActions>
            </Dialog>
            <Snackbar
                open={displaySuccessBar}
                autoHideDuration={2000}
                onClose={handleSnackbarClose}
            >
                <Alert icon={<CheckIcon fontSize="inherit" />} severity="success">
                    Item Saved Successfully
                </Alert>
            </Snackbar>
        </>
    );
}